// askSmart-ui/src/pages/MapleCoin/operations/TransactionsTab.tsx
//
// Every MapleCoin transaction, institution-wide, with the financial posting
// behind each one — and the maker/checker reversal workflow:
//   one Operations user requests a reversal with a reason (no money moves);
//   a DIFFERENT Operations user approves (the reversal executes) or rejects.
// The server and the database enforce the two-person rule; this screen only
// withholds the Approve button from the requester.
import { FormEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  Intervention, InterventionStatus, OpsApiError, OpsTransaction, OpsTransactionDetail, TransactionFilter,
  decideIntervention, getTransaction, listInterventions, requestReversal, searchTransactions,
} from './opsApi';
import { formatMoney, formatSignedAmount } from '../money';
import { Busy, Entries, Identity, KeyValues, Loading, Panel, Status, Unavailable, dateTime, dayStart, parseDecimalToMinor, titleCase } from './shared';
import { OpsNav } from './MapleCoinOperations';

const PAGE = 50;

const REFUSAL_TEXT: Record<string, string> = {
  NEGATIVE_BALANCE: 'Reversing it would overdraw the customer — the funds have already been used.',
  INVALID_TRANSITION: 'This transaction cannot be reversed in its current state.',
  INTERVENTION_OPEN: 'A reversal request for this transaction is already awaiting approval.',
  INTERVENTION_CONFLICT: 'This request has already been decided, or another request is open.',
  ACCOUNT_NOT_POSTABLE: 'An account this transaction touched is closed.',
  PRODUCTION_REFUSED: 'Sandbox transactions cannot be reversed in production.',
  SAME_APPROVER: 'You requested this reversal. A different Operations user must approve or reject it.',
  IDEMPOTENCY_CONFLICT: 'This submission conflicts with an earlier one. Close the form and try again.',
  NOT_FOUND: 'Not found.',
  NOT_ACTIVE: 'It was no longer active when approved (for example, the hold had already expired), so nothing changed.',
  HOLD_EXCEEDS_AVAILABLE: 'The hold is larger than the funds that can still be held (the balance less existing holds).',
  ACCOUNT_NOT_ACTIVE: 'The account is closed.',
};
export const refusal = (code: string | null | undefined, fallback = 'The request was refused.') => (code && REFUSAL_TEXT[code]) || fallback;

export function newKey() {
  const c: any = (globalThis as any).crypto;
  return typeof c?.randomUUID === 'function' ? `ops:${c.randomUUID()}` : `ops:${Date.now().toString(36)}:${Math.random().toString(36).slice(2)}`;
}

// ── Intervention card (approval queue, transaction detail, account controls) ──

const RESTRICTION_TEXT: Record<string, string> = { NO_OUTGOING: 'No outgoing transactions', NO_INCOMING: 'No incoming transactions' };
const REASON_TEXT: Record<string, string> = { FRAUD_REVIEW: 'Fraud review', COMPLIANCE_REVIEW: 'Compliance review', OPERATIONAL: 'Operational' };

/** What a request is, in words, and what approving it does. */
function describeIntervention(i: Intervention) {
  switch (i.type) {
    case 'HOLD_RELEASE':
      return {
        title: 'Hold release request',
        mine: 'You requested this release. A different Operations user must approve or reject it.',
        approveLabel: 'Approve and release hold',
        effect: <p>Approving releases hold <code>{i.target.holdReference}</code> now: <strong className="mco-num">{formatMoney(i.target.amountMinor, i.target.currency)}</strong> on account <code>{i.target.accountNumber}</code> becomes usable again.</p>,
      };
    case 'RESTRICTION_RELEASE':
      return {
        title: 'Restriction release request',
        mine: 'You requested this release. A different Operations user must approve or reject it.',
        approveLabel: 'Approve and lift restriction',
        effect: <p>Approving lifts restriction <code>{i.target.restrictionReference}</code> ({RESTRICTION_TEXT[i.target.restrictionType]}) on account <code>{i.target.accountNumber}</code> now.</p>,
      };
    default:
      return {
        title: 'Reversal request',
        mine: 'You requested this reversal. A different Operations user must approve or reject it.',
        approveLabel: 'Approve and execute reversal',
        effect: <p>Approving executes the reversal now: <strong className="mco-num">{formatSignedAmount(i.target.amountMinor, i.target.currency,
          i.target.direction === 'CREDIT' ? 'DEBIT' : 'CREDIT')}</strong> on account <code>{i.target.accountNumber}</code>.</p>,
      };
  }
}

function targetRows(i: Intervention, nav: OpsNav): [string, ReactNode][] {
  const account: [string, ReactNode] = ['Customer / account', <span key="account"><code>{i.target.customerNumber}</code> · <button type="button"
    className="mco-link" onClick={() => nav.openControls(i.target.accountNumber)}>{i.target.accountNumber}</button></span>];
  switch (i.type) {
    case 'HOLD_RELEASE':
      return [['Hold', <code key="hold">{i.target.holdReference}</code>], account,
        ['Amount held', <span key="amount" className="mco-num">{formatMoney(i.target.amountMinor, i.target.currency)}</span>],
        ['Hold reason', REASON_TEXT[i.target.reasonCode] ?? i.target.reasonCode],
        ['Hold status', <Status key="status" value={i.target.holdStatus} />]];
    case 'RESTRICTION_RELEASE':
      return [['Restriction', <span key="restriction"><code>{i.target.restrictionReference}</code> · {RESTRICTION_TEXT[i.target.restrictionType]}</span>], account,
        ['Restriction reason', REASON_TEXT[i.target.reasonCode] ?? i.target.reasonCode],
        ['Restriction status', <Status key="status" value={i.target.restrictionStatus} />]];
    default:
      return [['Transaction', <button key="transaction" type="button" className="mco-link" onClick={() => nav.openTransaction(i.target.transactionReference)}>{i.target.transactionReference}</button>],
        ['Customer / account', <span key="account"><code>{i.target.customerNumber}</code> · <code>{i.target.accountNumber}</code></span>],
        ['Amount', <span key="amount" className="mco-num">{formatSignedAmount(i.target.amountMinor, i.target.currency, i.target.direction)}</span>]];
  }
}

export function InterventionCard({ i, onDecided, nav }: { i: Intervention; onDecided: (i: Intervention) => void; nav: OpsNav }) {
  const [mode, setMode] = useState<'idle' | 'confirm-approve' | 'reject'>('idle');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = i.status === 'PENDING_APPROVAL';
  const about = describeIntervention(i);

  const decide = async (decision: 'APPROVE' | 'REJECT') => {
    setBusy(decision === 'APPROVE' ? 'approve' : 'reject');
    setError(null);
    try {
      onDecided(await decideIntervention(i.reference, decision, decision === 'REJECT' ? reason.trim() : undefined));
      setMode('idle');
    } catch (err) {
      setError(err instanceof OpsApiError && err.status < 500
        ? (err.code === 'SAME_APPROVER' ? about.mine : refusal(err.code, err.detail ?? undefined))
        : 'Operations is temporarily unavailable. Nothing was changed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className="mco-intervention" aria-label={`${about.title} ${i.reference}`}>
      <header>
        <strong>{about.title} {i.reference}</strong>
        <Status value={i.status} label={i.status === 'EXECUTION_REFUSED' ? 'Refused at execution' : undefined} />
      </header>
      <KeyValues rows={[
        ...targetRows(i, nav),
        ['Reason', i.reason],
        ['Requested', <span key="requested"><Identity sub={i.requestedBy.sub} you={i.requestedByYou} /> · {dateTime(i.requestedAt)}</span>],
        ...(i.decision ? [[i.decision.decision === 'APPROVED' ? 'Approved' : 'Rejected',
          <span key="decided"><Identity sub={i.decision.decidedBy.sub} you={i.decidedByYou} /> · {dateTime(i.decision.decidedAt)}</span>] as [string, ReactNode]] : []),
        ...(i.decision?.reason ? [['Decision reason', i.decision.reason] as [string, string]] : []),
        ...(i.type === 'TRANSACTION_REVERSAL' && i.execution?.outcome === 'EXECUTED' ? [['Reversal', <button key="reversal" type="button" className="mco-link"
          onClick={() => nav.openTransaction(i.execution!.resultTransactionReference!)}>{i.execution.resultTransactionReference}</button>] as [string, ReactNode]] : []),
        ...(i.execution?.outcome === 'REFUSED' ? [['Execution', refusal(i.execution.refusalCode, i.execution.refusalMessage ?? undefined)] as [string, string]] : []),
      ]} />

      {pending && i.requestedByYou && <p className="mco-note">{about.mine}</p>}
      {pending && !i.requestedByYou && mode === 'idle' && (
        <div className="mco-actions">
          <button type="button" className="mco-btn mco-btn--primary" onClick={() => setMode('confirm-approve')}>Approve</button>
          <button type="button" className="mco-btn" onClick={() => setMode('reject')}>Reject</button>
        </div>
      )}
      {pending && !i.requestedByYou && mode === 'confirm-approve' && (
        <div className="mco-confirm" role="group" aria-label="Confirm approval">
          {about.effect}
          <div className="mco-actions">
            <button type="button" className="mco-btn mco-btn--primary" disabled={busy !== null} onClick={() => decide('APPROVE')}>
              <Busy busy={busy === 'approve'}>{about.approveLabel}</Busy>
            </button>
            <button type="button" className="mco-btn mco-btn--quiet" disabled={busy !== null} onClick={() => setMode('idle')}>Cancel</button>
          </div>
        </div>
      )}
      {pending && !i.requestedByYou && mode === 'reject' && (
        <div className="mco-confirm" role="group" aria-label="Reject request">
          <label>Reason for rejecting (required)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} />
          </label>
          <div className="mco-actions">
            <button type="button" className="mco-btn" disabled={busy !== null || !reason.trim()} onClick={() => decide('REJECT')}>
              <Busy busy={busy === 'reject'}>Reject request</Busy>
            </button>
            <button type="button" className="mco-btn mco-btn--quiet" disabled={busy !== null} onClick={() => setMode('idle')}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p className="mco-error" role="alert">{error}</p>}
    </article>
  );
}

// ── Reversal request (the maker) ─────────────────────────────────────────────

function RequestReversal({ t, onRequested }: { t: OpsTransactionDetail; onRequested: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  // One key per submission attempt: a retry of the same form cannot create a
  // second request.
  const [key, setKey] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!t.reversal.eligible) {
    return <p className="mco-note" data-testid="reversal-ineligible">Not reversible: {refusal(t.reversal.refusalCode, t.reversal.message ?? undefined)}</p>;
  }
  if (!open) {
    return <button type="button" className="mco-btn" onClick={() => { setOpen(true); setKey(newKey()); }}>Request reversal</button>;
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestReversal(t.reference, reason.trim(), key);
      setOpen(false);
      setReason('');
      onRequested();
    } catch (err) {
      setError(err instanceof OpsApiError && err.status < 500 ? refusal(err.code, err.detail ?? undefined) : 'Operations is temporarily unavailable. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="mco-confirm" onSubmit={submit} aria-label="Request reversal">
      <p>Request a reversal of <strong className="mco-num">{formatSignedAmount(t.amountMinor, t.currency, t.direction)}</strong>.
        No money moves until a different Operations user approves it.</p>
      <label>Reason (required)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} />
      </label>
      <div className="mco-actions">
        <button type="submit" className="mco-btn mco-btn--primary" disabled={busy || !reason.trim()}>
          <Busy busy={busy}>Submit for approval</Busy>
        </button>
        <button type="button" className="mco-btn mco-btn--quiet" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {error && <p className="mco-error" role="alert">{error}</p>}
    </form>
  );
}

// ── Transaction detail ───────────────────────────────────────────────────────

function TransactionDetailView({ reference, onBack, nav }: { reference: string; onBack: () => void; nav: OpsNav }) {
  const [t, setT] = useState<OpsTransactionDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'missing'>('loading');

  const load = useCallback(async () => {
    try {
      setT(await getTransaction(reference));
      setState('ready');
    } catch (err) {
      setState(err instanceof OpsApiError && err.status === 404 ? 'missing' : 'error');
    }
  }, [reference]);
  useEffect(() => { setState('loading'); load(); }, [load]);

  return (
    <div className="mco-stack">
      <button type="button" className="mco-back" onClick={onBack}><ArrowLeft size={14} /> Transactions</button>
      {state === 'loading' && <Loading />}
      {state === 'error' && <Unavailable onRetry={load} />}
      {state === 'missing' && <Panel><p>Transaction {reference} was not found.</p></Panel>}
      {state === 'ready' && t && (
        <>
          <Panel title={`Transaction ${t.reference}`} actions={<Status value={t.status} />}>
            <p className="mco-figure mco-num" data-testid="txn-amount">{formatSignedAmount(t.amountMinor, t.currency, t.direction)}</p>
            <KeyValues rows={[
              ['Customer', <code>{t.customerNumber}</code>],
              ['Account', <code>{t.accountNumber}</code>],
              ['Type', titleCase(t.type)],
              ['Rail / source', `${titleCase(t.rail)} · ${t.externalSource}`],
              ['External reference', <code>{t.externalReference}</code>],
              ['Direction', t.direction === 'CREDIT' ? 'Credit (money in)' : 'Debit (money out)'],
              ['Initiated by', <span>{titleCase(t.initiatedBy.type)} · <Identity sub={t.initiatedBy.id} /></span>],
              ['Created', dateTime(t.createdAt)],
              ['Status changed', dateTime(t.statusChangedAt)],
              ['Reason', t.reason],
              ...(t.reversesReference ? [['Reverses', <button type="button" className="mco-link" onClick={() => nav.openTransaction(t.reversesReference!)}>{t.reversesReference}</button>] as [string, ReactNode]] : []),
              ...(t.reversedByReference ? [['Reversed by', <button type="button" className="mco-link" onClick={() => nav.openTransaction(t.reversedByReference!)}>{t.reversedByReference}</button>] as [string, ReactNode]] : []),
            ]} />
          </Panel>

          <Panel title="Lifecycle">
            <ol className="mco-lifecycle">
              {t.events.map((e, i) => (
                <li key={i}>
                  <Status value={e.toStatus} /> <span>{titleCase(e.action)}</span>
                  <span className="mco-dim"> · {titleCase(e.actorType)} <Identity sub={e.actorId} /> · {dateTime(e.occurredAt)}
                    {e.journalSeq ? ` · journal #${e.journalSeq}` : ''}</span>
                  {e.reason && <div className="mco-dim">{e.reason}</div>}
                </li>
              ))}
            </ol>
          </Panel>

          <Panel title="Financial posting" label="Financial posting">
            {t.postings.map((j) => (
              <div key={j.journalSeq} className="mco-journal">
                <p className="mco-journal-head">
                  <button type="button" className="mco-link" onClick={() => nav.openJournal(j.journalSeq)}>Journal #{j.journalSeq}</button>
                  <span> · {titleCase(j.journalType)} · {j.purpose ? titleCase(j.purpose) : ''} · posted {dateTime(j.postedAt)} · seal #{j.sealSeq ?? '—'}</span>
                </p>
                <Entries entries={j.entries} />
              </div>
            ))}
          </Panel>

          <Panel title="Reversal">
            {t.interventions.map((i) => (
              <InterventionCard key={i.reference} i={i} nav={nav} onDecided={() => load()} />
            ))}
            <RequestReversal t={t} onRequested={load} />
          </Panel>
        </>
      )}
    </div>
  );
}

// ── Approval queue ───────────────────────────────────────────────────────────

const QUEUE: { key: InterventionStatus | ''; label: string }[] = [
  { key: 'PENDING_APPROVAL', label: 'Awaiting approval' },
  { key: 'EXECUTED', label: 'Executed' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'EXECUTION_REFUSED', label: 'Refused at execution' },
  { key: '', label: 'All' },
];

function ApprovalQueue({ nav }: { nav: OpsNav }) {
  const [status, setStatus] = useState<InterventionStatus | ''>('PENDING_APPROVAL');
  const [items, setItems] = useState<Intervention[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(async () => {
    try {
      const r = await listInterventions(status || undefined);
      setItems(r.interventions);
      setCounts(r.counts);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [status]);
  useEffect(() => { setState('loading'); load(); }, [load]);

  return (
    <Panel title="Reversal requests" label="Reversal requests">
      <div className="mco-segments" role="tablist">
        {QUEUE.map((q) => (
          <button key={q.key || 'all'} type="button" role="tab" aria-selected={status === q.key}
            className={`mco-seg${status === q.key ? ' mco-seg--on' : ''}`} onClick={() => setStatus(q.key)}>
            {q.label}{q.key && counts[q.key] !== undefined ? ` (${counts[q.key]})` : ''}
          </button>
        ))}
      </div>
      {state === 'loading' && <Loading />}
      {state === 'error' && <Unavailable onRetry={load} />}
      {state === 'ready' && items.length === 0 && <p className="mco-empty">No reversal requests here.</p>}
      {state === 'ready' && items.map((i) => <InterventionCard key={i.reference} i={i} nav={nav} onDecided={() => load()} />)}
    </Panel>
  );
}

// ── List and filters ─────────────────────────────────────────────────────────

type Form = { from: string; to: string; reference: string; customerNumber: string; accountNumber: string; type: string;
  status: string; direction: string; minAmount: string; maxAmount: string };
const EMPTY: Form = { from: '', to: '', reference: '', customerNumber: '', accountNumber: '', type: '', status: '',
  direction: '', minAmount: '', maxAmount: '' };

export default function TransactionsTab({ nav, initialReference, initialApprovals, initialFilter }: {
  nav: OpsNav; initialReference?: string; initialApprovals?: boolean;
  initialFilter?: { accountNumber?: string; customerNumber?: string };
}) {
  const [mode, setMode] = useState<'list' | 'approvals'>(initialApprovals ? 'approvals' : 'list');
  const [openRef, setOpenRef] = useState<string | null>(initialReference ?? null);
  const [form, setForm] = useState<Form>({ ...EMPTY, ...initialFilter });
  const [applied, setApplied] = useState<TransactionFilter>({ ...initialFilter });
  const [rows, setRows] = useState<OpsTransaction[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'invalid'>('loading');
  const [more, setMore] = useState(false);

  const load = useCallback(async (f: TransactionFilter, before?: string) => {
    if (before) setMore(true); else setState('loading');
    try {
      const r = await searchTransactions({ ...f, limit: String(PAGE), before });
      setRows((prev) => (before ? [...prev, ...r.transactions] : r.transactions));
      setTypes(r.types);
      setNext(r.nextBefore);
      setState('ready');
    } catch (err) {
      setState(err instanceof OpsApiError && err.status === 400 ? 'invalid' : 'error');
    } finally {
      setMore(false);
    }
  }, []);
  useEffect(() => { if (mode === 'list') load(applied); }, [mode, load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (openRef) return <TransactionDetailView reference={openRef} onBack={() => setOpenRef(null)} nav={{ ...nav, openTransaction: setOpenRef }} />;

  const apply = (e?: FormEvent) => {
    e?.preventDefault();
    const min = form.minAmount.trim() ? parseDecimalToMinor(form.minAmount) : undefined;
    const max = form.maxAmount.trim() ? parseDecimalToMinor(form.maxAmount) : undefined;
    if (min === null || max === null) {
      setState('invalid');
      return;
    }
    const f: TransactionFilter = {
      from: dayStart(form.from), to: dayStart(form.to, true), reference: form.reference.trim() || undefined,
      customerNumber: form.customerNumber.trim() || undefined, accountNumber: form.accountNumber.trim() || undefined,
      type: form.type || undefined, status: form.status || undefined, direction: form.direction || undefined,
      minAmountMinor: min, maxAmountMinor: max,
    };
    setApplied(f);
    load(f);
  };
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="mco-stack">
      <div className="mco-segments" role="tablist" aria-label="Transactions view">
        <button type="button" role="tab" aria-selected={mode === 'list'} className={`mco-seg${mode === 'list' ? ' mco-seg--on' : ''}`}
          onClick={() => setMode('list')}>All transactions</button>
        <button type="button" role="tab" aria-selected={mode === 'approvals'} className={`mco-seg${mode === 'approvals' ? ' mco-seg--on' : ''}`}
          onClick={() => setMode('approvals')}>Reversal requests</button>
      </div>

      {mode === 'approvals' && <ApprovalQueue nav={{ ...nav, openTransaction: setOpenRef }} />}

      {mode === 'list' && (
        <>
          <Panel title="Transactions" label="Transaction filters">
            <form className="mco-filters" onSubmit={apply} aria-label="Filter transactions">
              <label>From<input type="date" value={form.from} onChange={set('from')} /></label>
              <label>To<input type="date" value={form.to} onChange={set('to')} /></label>
              <label>Reference<input value={form.reference} onChange={set('reference')} placeholder="T…" /></label>
              <label>Customer number<input value={form.customerNumber} onChange={set('customerNumber')} placeholder="C…" /></label>
              <label>Account number<input value={form.accountNumber} onChange={set('accountNumber')} inputMode="numeric" /></label>
              <label>Type
                <select value={form.type} onChange={set('type')}>
                  <option value="">Any type</option>
                  {types.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                </select>
              </label>
              <label>Status
                <select value={form.status} onChange={set('status')}>
                  <option value="">Any status</option>
                  {['CREATED', 'PENDING', 'COMPLETED', 'FAILED', 'REVERSED'].map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                </select>
              </label>
              <label>Direction
                <select value={form.direction} onChange={set('direction')}>
                  <option value="">Either</option>
                  <option value="CREDIT">Credit (in)</option>
                  <option value="DEBIT">Debit (out)</option>
                </select>
              </label>
              <label>Min amount<input value={form.minAmount} onChange={set('minAmount')} inputMode="decimal" placeholder="0.00" /></label>
              <label>Max amount<input value={form.maxAmount} onChange={set('maxAmount')} inputMode="decimal" placeholder="0.00" /></label>
              <div className="mco-filter-actions">
                <button type="submit" className="mco-btn">Apply</button>
                <button type="button" className="mco-btn mco-btn--quiet" onClick={() => { setForm(EMPTY); setApplied({}); load({}); }}>Clear</button>
              </div>
            </form>
          </Panel>

          <Panel label="Transaction list">
            {state === 'loading' && <Loading />}
            {state === 'error' && <Unavailable onRetry={() => load(applied)} />}
            {state === 'invalid' && <p className="mco-error" role="alert">One of the filters is not valid. Amounts are like 1,250.00; dates use the picker.</p>}
            {state === 'ready' && (
              <>
                <div className="mco-table-wrap">
                  <table className="mco-table">
                    <thead><tr><th>Created</th><th>Reference</th><th>Customer</th><th>Account</th><th>Type</th><th>Status</th><th className="mco-r">Amount</th></tr></thead>
                    <tbody>
                      {rows.length === 0 && <tr><td colSpan={7} className="mco-empty">No transactions match.</td></tr>}
                      {rows.map((r) => (
                        <tr key={r.reference}>
                          <td className="mco-nowrap">{dateTime(r.createdAt)}</td>
                          <td><button type="button" className="mco-link" onClick={() => setOpenRef(r.reference)}>{r.reference}</button></td>
                          <td><code>{r.customerNumber}</code></td>
                          <td><code>{r.accountNumber}</code></td>
                          <td>{titleCase(r.type)}</td>
                          <td><Status value={r.status} /></td>
                          <td className="mco-r mco-num">{formatSignedAmount(r.amountMinor, r.currency, r.direction)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {next && (
                  <button type="button" className="mco-btn" disabled={more} onClick={() => load(applied, next)}>
                    <Busy busy={more}>Load more</Busy>
                  </button>
                )}
              </>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

